/**
 * The box an embedded plugin component lives in.
 *
 * Deliberately no transform / contain / container-type, so position:fixed
 * dialogs inside embedded pages still escape to the viewport. Publishes its
 * size as CSS variables — `--nc-page-height` lets pages written for the full
 * viewport opt into fitting the frame:
 *   height: var(--nc-page-height, calc(100dvh - var(--nc-top-nav-height, 64px)))
 */

import React, { useLayoutEffect, useRef } from 'react';
import './embed-frame.css';

export function EmbedFrame({
  kind, className, style, children,
}: { kind: string; className?: string; style?: React.CSSProperties; children: React.ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const publish = () => {
      el.style.setProperty('--nc-embed-width', `${el.clientWidth}px`);
      el.style.setProperty('--nc-embed-height', `${el.clientHeight}px`);
      el.style.setProperty('--nc-page-height', `${el.clientHeight}px`);
    };
    publish();
    if (typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(publish);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <div
      ref={ref}
      className={['nc-embed-frame', className].filter(Boolean).join(' ')}
      data-nc-embed="true"
      data-nc-embed-kind={kind}
      style={style}
    >
      {children}
    </div>
  );
}
